import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import '../styles/SecurityNews.css';

const FALLBACK_NEWS = [
  {
    title: 'Critical Apache Struts RCE Vulnerability Actively Exploited in the Wild',
    summary: 'A newly disclosed remote code execution flaw in Apache Struts is being weaponized by threat actors within 48 hours of disclosure, security researchers confirm.',
    category: 'Exploit',
    time: '2h ago',
    url: 'https://thehackernews.com',
    severity: 'critical',
  },
  {
    title: 'CISA Adds Five New Vulnerabilities to Known Exploited Vulnerabilities Catalog',
    summary: 'The US cybersecurity agency expands its KEV catalog with five actively exploited flaws affecting enterprise software from Microsoft, Ivanti, and Fortinet.',
    category: 'CISA',
    time: '4h ago',
    url: 'https://thehackernews.com',
    severity: 'high',
  },
  {
    title: 'North Korean Hackers Deploy New Malware Targeting Software Supply Chains',
    summary: 'The Lazarus Group has been linked to a sophisticated new campaign injecting malicious packages into open-source repositories used by thousands of developers.',
    category: 'Threat Actor',
    time: '6h ago',
    url: 'https://thehackernews.com',
    severity: 'high',
  },
  {
    title: 'New Ransomware Group Claims 40+ Victims in First Month of Operations',
    summary: 'A previously unknown ransomware-as-a-service operation named "NullLock" has emerged with a double-extortion model targeting healthcare and finance sectors.',
    category: 'Ransomware',
    time: '9h ago',
    url: 'https://thehackernews.com',
    severity: 'high',
  },
  {
    title: 'OpenSSH Vulnerability Allows Authentication Bypass on Misconfigured Servers',
    summary: "A logic flaw in OpenSSH's handling of keyboard-interactive authentication can be abused to bypass password checks under specific server configurations.",
    category: 'CVE',
    time: '12h ago',
    url: 'https://thehackernews.com',
    severity: 'medium',
  },
  {
    title: 'Google Chrome Patches Zero-Day Used in Targeted Espionage Campaign',
    summary: 'Google has shipped an emergency update for Chrome after confirming a type confusion bug in the V8 engine was being exploited by a state-sponsored threat actor.',
    category: 'Zero-Day',
    time: '15h ago',
    url: 'https://thehackernews.com',
    severity: 'critical',
  },
];

const severityColor = {
  critical: '#E24B4A',
  high: '#EF9F27',
  medium: '#BA7517',
  low: '#1D9E75',
};

const formatAge = (date) => {
  const diff = Math.floor((Date.now() - date) / 60000);
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

function NewsCard({ item, i }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  const handleTilt = useCallback((e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    card.style.transform = `perspective(600px) rotateY(${x * 8}deg) rotateX(${-y * 6}deg) translateY(-4px)`;
  }, []);

  const handleTiltReset = useCallback((e) => {
    e.currentTarget.style.transform = 'perspective(600px) rotateY(0deg) rotateX(0deg) translateY(0px)';
  }, []);

  return (
    <motion.a
      ref={ref}
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="news-card"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.42, delay: i * 0.07 }}
      onMouseMove={handleTilt}
      onMouseLeave={handleTiltReset}
    >
      <div className="news-card__top">
        <span
          className="news-card__cat font-mono"
          style={{ color: severityColor[item.severity], background: `${severityColor[item.severity]}15` }}
        >
          {item.category}
        </span>
        <span className="news-card__time font-mono">{item.time}</span>
      </div>
      <h3 className="news-card__title">{item.title}</h3>
      <p className="news-card__summary">{item.summary}</p>
      <div className="news-card__footer">
        <span className="news-card__read">Read more →</span>
        <span className="news-card__sev-dot" style={{ background: severityColor[item.severity] }} />
      </div>
    </motion.a>
  );
}

export default function SecurityNews() {
  const [news, setNews] = useState(FALLBACK_NEWS);
  const [lastFetch, setLastFetch] = useState(null);
  const [fetching, setFetching] = useState(false);
  const headRef = useRef(null);
  const inView = useInView(headRef, { once: true, margin: '-80px' });
  const fetchRef = useRef(null);

  fetchRef.current = async () => {
    setFetching(true);
    try {
      const res = await fetch(
        'https://api.rss2json.com/v1/api.json?rss_url=https%3A%2F%2Ffeeds.feedburner.com%2FTheHackersNews&count=6'
      );
      const data = await res.json();
      if (data.status === 'ok' && data.items?.length) {
        const parsed = data.items.map((item, idx) => ({
          title: item.title,
          summary: item.description?.replace(/<[^>]+>/g, '').slice(0, 160) + '...',
          category: ['Exploit', 'CVE', 'Threat Actor', 'Ransomware', 'Zero-Day', 'CISA'][idx % 6],
          time: formatAge(new Date(item.pubDate)),
          url: item.link,
          severity: ['critical', 'high', 'high', 'medium', 'critical', 'high'][idx % 6],
        }));
        setNews(parsed);
        setLastFetch(new Date());
      }
    } catch {
      setNews(FALLBACK_NEWS);
    } finally {
      setFetching(false);
    }
  };

  const doFetch = useCallback(() => fetchRef.current(), []);

  useEffect(() => {
    doFetch();
    const interval = setInterval(doFetch, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [doFetch]);

  return (
    <section id="news" className="news section">
      <div className="container">
        <div className="news__head" ref={headRef}>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45 }}
          >
            <div className="section-eyebrow">Security News</div>
            <h2 className="news__h2">Live threat intelligence feed</h2>
            <p className="news__sub">
              Refreshed every hour from The Hacker News. Stay ahead of the threat landscape.
            </p>
          </motion.div>

          <motion.div
            className="news__meta"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.3 }}
          >
            <span className={`news__status ${fetching ? 'news__status--fetching' : ''}`}>
              <span className="news__status-dot" />
              {fetching ? 'Fetching...' : lastFetch ? `Updated ${formatAge(lastFetch)}` : 'Live'}
            </span>
            <button className="news__refresh" onClick={doFetch} disabled={fetching}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 7a6 6 0 016-6 6 6 0 014.9 2.5M13 7a6 6 0 01-6 6 6 6 0 01-4.9-2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M10.5 2L11.5 4.5H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Refresh
            </button>
          </motion.div>
        </div>

        <div className="news__grid">
          {news.map((item, i) => (
            <NewsCard key={i} item={item} i={i} />
          ))}
        </div>

        <motion.div
          className="news__more"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
        >
          <a
            href="https://thehackernews.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            View all on The Hacker News →
          </a>
        </motion.div>
      </div>
    </section>
  );
}
